import { $ProjectedEntity } from '../__generated/enhanced-model.js';
import { implementCustomExpressions } from '../__generated/implementation.js';
import { HandrailType, PositionType } from '../__generated/model/index.js';
import { FixingSystemDataBindings } from '../data-bindings/fixing-system.js';
import { HandrailEndBindings } from '../data-bindings/handrail-end.js';
import { HandrailBindings } from '../data-bindings/handrail.js';
import { ObjectNames, getObjectInfo } from '../data-bindings/objects.js';
import { PostDataBindings } from '../data-bindings/post.js';
import { ParentInheritExpressioonBuilder } from '../sdk/data-binding.js';
export const projectedEntityExpressions = implementCustomExpressions({
    ProjectedEntity: {
        $postType: ParentInheritExpressioonBuilder.from($ProjectedEntity).inherit('$deckItem').inherit('$deck').use('postType').bind(),
        $postShape: ParentInheritExpressioonBuilder.from($ProjectedEntity).inherit('$deckItem').inherit('$deck').use('$postShape').bind(),
        $postStyle: ParentInheritExpressioonBuilder.from($ProjectedEntity).inherit('$deckItem').inherit('$deck').use('postStyle').bind(),
        $postFinish: ParentInheritExpressioonBuilder.from($ProjectedEntity).inherit('$deckItem').inherit('$deck').use('postStainlessFinish').bind(),
        $handrailType: ParentInheritExpressioonBuilder.from($ProjectedEntity).inherit('$deckItem').inherit('$deck').use('handrailType').bind(),
        $postSize: ParentInheritExpressioonBuilder.from($ProjectedEntity).inherit('$deckItem').inherit('$deck').use('$postSize').bind(),
        $skus: (s)=>{
            return s.$parts.flatMap((part)=>{
                return part.skus;
            });
        },
        $partObjects: (s)=>{
            return s.$parts.flatMap((part)=>{
                return part.objects;
            });
        }
    },
    Post: {
        $thinIntermediatePostType: ParentInheritExpressioonBuilder.from($ProjectedEntity).inherit('$deckItem').inherit('$deck').use('thinIntermediatePostType').bind(),
        $parts: PostDataBindings.parts.bind()
    },
    FixingSystem: {
        $fixingSystemType: ParentInheritExpressioonBuilder.from($ProjectedEntity).inherit('$deckItem').inherit('$deck').use('fixingSystem').bind(),
        $finishType: ParentInheritExpressioonBuilder.from($ProjectedEntity).inherit('$deckItem').inherit('$deck').use('finish').bind(),
        $numberOfWires: ParentInheritExpressioonBuilder.from($ProjectedEntity).inherit('$deckItem').inherit('$deck').use('numberOfWires').bind(),
        $offsetFromBottom: (s)=>{
            const minHeight = 0;
            const maxHeight = 950;
            const wireHeightStep = (maxHeight - minHeight) / (s.$deckItem.$deck.$numberOfWires + 1);
            return maxHeight - (s.order + 1) * wireHeightStep;
        },
        $wireSkuLength: (s)=>{
            return s.rightX - s.leftX - s.$postSize;
        },
        $wireSkuLengthMetadata: (s)=>{
            return {
                length: s.$wireSkuLength
            };
        },
        $partSkus: (s)=>{
            return [
                ...s.$fixingSystemAttachementData.fixingSystemSkus.map((sku)=>({
                        sku,
                        metadata: {}
                    })),
                {
                    sku: s.$fixingSystemAttachementData.wireSku,
                    metadata: s.$wireSkuLengthMetadata
                }
            ];
        },
        $fixingSystemPositionType: FixingSystemDataBindings.fixingSystemPositionType.bind('fixingSystemPositionType'),
        $fixingSystemAttachementType: FixingSystemDataBindings.fixingSystemAttachementData.bind('fixingSystemAttachementType'),
        $fixingSystemAttachementData: FixingSystemDataBindings.fixingSystemAttachementData.bind(),
        $flatParts: (s)=>{
            const leftFixingPartX = s.leftX + s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset;
            const rightFixingPartX = s.rightX - s.$postSize / 2 - s.$fixingSystemAttachementData.partCenterOffset;
            const wireLength = rightFixingPartX - leftFixingPartX;
            const fixingSystemObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.fixingSystemObjectName);
            const wireObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.wireObjectName);
            return [
                {
                    skus: s.$partSkus,
                    objects: [
                        {
                            key: `${s.key}_left`,
                            x: leftFixingPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: s.leftZ,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_wire`,
                            y: s.leftY + s.$offsetFromBottom,
                            z: s.leftZ,
                            x: s.leftX + s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset,
                            fileUrl: wireObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.wireObjectName,
                            material: s.$fixingSystemAttachementData.material,
                            morphTargetInfluences: {
                                length: {
                                    value: wireLength,
                                    scaleZero: s.$fixingSystemAttachementData.scaleZero,
                                    scaleOne: s.$fixingSystemAttachementData.scaleOne
                                }
                            }
                        },
                        {
                            key: `${s.key}_right`,
                            x: rightFixingPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: s.leftZ,
                            rotationY: Math.PI,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            material: s.$fixingSystemAttachementData.material
                        }
                    ]
                }
            ];
        },
        $obtuseRoundParts: (s)=>{
            const leftFixingPartX = s.leftX + s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset;
            const rightFixingPartX = s.rightX - s.$postSize / 2 - s.$fixingSystemAttachementData.partCenterOffset;
            const wireLength = rightFixingPartX - leftFixingPartX;
            const fixingSystemObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.fixingSystemObjectName);
            const wireObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.wireObjectName);
            return [
                {
                    skus: s.$partSkus,
                    objects: [
                        {
                            key: `${s.key}_left`,
                            x: leftFixingPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: s.leftZ,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_wire`,
                            y: s.leftY + s.$offsetFromBottom,
                            z: s.leftZ,
                            x: s.leftX + s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset,
                            fileUrl: wireObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.wireObjectName,
                            material: s.$fixingSystemAttachementData.material,
                            morphTargetInfluences: {
                                length: {
                                    value: wireLength,
                                    scaleZero: s.$fixingSystemAttachementData.scaleZero,
                                    scaleOne: s.$fixingSystemAttachementData.scaleOne
                                }
                            }
                        },
                        {
                            key: `${s.key}_right`,
                            x: rightFixingPartX,
                            rotationY: Math.PI,
                            y: s.leftY + s.$offsetFromBottom,
                            z: s.leftZ,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            material: s.$fixingSystemAttachementData.material
                        }
                    ]
                }
            ];
        },
        $obtuseSquareRotatedParts: (s)=>{
            const normalizedAngle = s.rightAngle - Math.PI;
            const leftFixingPartX = s.leftX + s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset;
            const leftFixingPartZ = s.leftZ;
            const rightFixingPartX = s.rightX - (s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset) * Math.cos(normalizedAngle);
            const rightFixingPartZ = s.leftZ + (s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset) * Math.sin(normalizedAngle);
            const wireLength = Math.sqrt((rightFixingPartX - leftFixingPartX) ** 2 + (rightFixingPartZ - leftFixingPartZ) ** 2);
            const wireRotationY = -Math.atan((rightFixingPartZ - leftFixingPartZ) / (rightFixingPartX - leftFixingPartX));
            const fixingSystemObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.fixingSystemObjectName);
            const wireObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.wireObjectName);
            return [
                {
                    skus: s.$partSkus,
                    objects: [
                        {
                            key: `${s.key}_left_square_rotated`,
                            x: leftFixingPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: leftFixingPartZ,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            rotationX: Math.PI / 2,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_wire_square_rotated`,
                            x: leftFixingPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: leftFixingPartZ,
                            fileUrl: wireObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.wireObjectName,
                            material: s.$fixingSystemAttachementData.material,
                            rotationX: Math.PI / 2,
                            rotationZ: -wireRotationY,
                            morphTargetInfluences: {
                                length: {
                                    value: wireLength,
                                    scaleZero: s.$fixingSystemAttachementData.scaleZero,
                                    scaleOne: s.$fixingSystemAttachementData.scaleOne
                                }
                            }
                        },
                        {
                            key: `${s.key}_right_square_rotated`,
                            x: rightFixingPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: rightFixingPartZ,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            rotationX: Math.PI / 2,
                            rotationZ: -(Math.PI + normalizedAngle),
                            material: s.$fixingSystemAttachementData.material
                        }
                    ]
                }
            ];
        },
        $obtuseSquareAdaptorParts: (s)=>{
            const normalizedAngle = s.rightAngle - Math.PI;
            const leftFixingPartX = s.leftX + s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset;
            const leftFixingPartZ = s.leftZ;
            const rightFixingPartX = s.rightX - (s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset) * Math.cos(normalizedAngle);
            const rightFixingPartZ = s.leftZ + (s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset) * Math.sin(normalizedAngle);
            const adaptorScrewSize = 7;
            const adaptorHandlerInternalSpice = 4;
            const adaptorScrewPartX = s.rightX - (s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset + adaptorScrewSize) * Math.cos(normalizedAngle);
            const adaptorScrewPartZ = s.leftZ + (s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset + adaptorScrewSize) * Math.sin(normalizedAngle);
            const wireLength = Math.sqrt((adaptorScrewPartX - leftFixingPartX) ** 2 + (adaptorScrewPartZ - leftFixingPartZ) ** 2) - adaptorHandlerInternalSpice;
            const wireRotationY = -Math.atan((adaptorScrewPartZ - leftFixingPartZ) / (adaptorScrewPartX - leftFixingPartX));
            const adaptorHandlerPartX = leftFixingPartX + (wireLength + adaptorHandlerInternalSpice) * Math.cos(wireRotationY);
            const adaptorHandlerPartZ = leftFixingPartZ - (wireLength + adaptorHandlerInternalSpice) * Math.sin(wireRotationY);
            const fixingSystemObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.fixingSystemObjectName);
            const wireObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.wireObjectName);
            const adaptorHandlerObjectInfo = getObjectInfo(ObjectNames.wireStainlessSteel);
            const adaptorScrewObjectInfo = getObjectInfo(ObjectNames.wireStainlessSteelRotatable);
            return [
                {
                    skus: s.$partSkus,
                    objects: [
                        {
                            key: `${s.key}_left_square_adaptor`,
                            x: leftFixingPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: leftFixingPartZ,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_wire_square_adaptor`,
                            x: leftFixingPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: leftFixingPartZ,
                            fileUrl: wireObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.wireObjectName,
                            material: s.$fixingSystemAttachementData.material,
                            rotationY: wireRotationY,
                            morphTargetInfluences: {
                                length: {
                                    value: wireLength,
                                    scaleZero: s.$fixingSystemAttachementData.scaleZero,
                                    scaleOne: s.$fixingSystemAttachementData.scaleOne
                                }
                            }
                        },
                        {
                            key: `${s.key}_right_square_adaptor`,
                            x: rightFixingPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: rightFixingPartZ,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            rotationY: Math.PI + normalizedAngle,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_right_square_adaptor_screw`,
                            x: adaptorScrewPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: adaptorScrewPartZ,
                            fileUrl: adaptorScrewObjectInfo.fileUrl,
                            objectName: ObjectNames.wireStainlessSteelRotatable,
                            rotationX: Math.PI / 2,
                            rotationZ: -normalizedAngle,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_right_square_adaptor_handler`,
                            x: adaptorHandlerPartX,
                            y: s.leftY + s.$offsetFromBottom,
                            z: adaptorHandlerPartZ,
                            fileUrl: adaptorHandlerObjectInfo.fileUrl,
                            objectName: ObjectNames.wireStainlessSteel,
                            rotationX: Math.PI / 2,
                            rotationY: 0,
                            rotationZ: -wireRotationY,
                            material: s.$fixingSystemAttachementData.material
                        }
                    ]
                }
            ];
        },
        $stairsSimpleParts: (s)=>{
            const leftFixingPartX = s.leftX + s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset;
            const rightFixingPartX = s.rightX - s.$postSize / 2 - s.$fixingSystemAttachementData.partCenterOffset;
            const leftWireY = s.leftY + s.$offsetFromBottom;
            const rightWireY = s.rightY + s.$offsetFromBottom;
            const wireLength = Math.sqrt((rightFixingPartX - leftFixingPartX) ** 2 + (rightWireY - leftWireY) ** 2);
            const wireSlope = Math.atan((rightWireY - leftWireY) / (rightFixingPartX - leftFixingPartX));
            const fixingSystemObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.fixingSystemObjectName);
            const wireObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.wireObjectName);
            return [
                {
                    skus: s.$partSkus,
                    objects: [
                        {
                            key: `${s.key}_left_stairs_none`,
                            x: s.leftZ,
                            y: leftWireY,
                            z: leftFixingPartX,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            rotationY: -Math.PI / 2,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_wire_stairs_none`,
                            x: s.leftZ,
                            y: leftWireY,
                            z: leftFixingPartX,
                            rotationY: -Math.PI / 2,
                            rotationZ: wireSlope,
                            fileUrl: wireObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.wireObjectName,
                            material: s.$fixingSystemAttachementData.material,
                            morphTargetInfluences: {
                                length: {
                                    value: wireLength,
                                    scaleZero: s.$fixingSystemAttachementData.scaleZero,
                                    scaleOne: s.$fixingSystemAttachementData.scaleOne
                                }
                            }
                        },
                        {
                            key: `${s.key}_right_stairs_none`,
                            x: s.leftZ,
                            y: rightWireY,
                            z: rightFixingPartX,
                            rotationY: Math.PI / 2,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            material: s.$fixingSystemAttachementData.material
                        }
                    ]
                }
            ];
        },
        $stairsRotatedParts: (s)=>{
            const leftFixingPartX = s.leftX + s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset;
            const rightFixingPartX = s.rightX - s.$postSize / 2 - s.$fixingSystemAttachementData.partCenterOffset;
            const leftWireY = s.leftY + s.$offsetFromBottom;
            const rightWireY = s.rightY + s.$offsetFromBottom;
            const wireLength = Math.sqrt((rightFixingPartX - leftFixingPartX) ** 2 + (rightWireY - leftWireY) ** 2);
            const wireSlope = Math.atan((rightWireY - leftWireY) / (rightFixingPartX - leftFixingPartX));
            const fixingSystemObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.fixingSystemObjectName);
            const wireObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.wireObjectName);
            return [
                {
                    skus: s.$partSkus,
                    objects: [
                        {
                            key: `${s.key}_left_stairs_rotated`,
                            x: s.leftZ,
                            y: leftWireY,
                            z: leftFixingPartX,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            rotationY: -Math.PI / 2,
                            rotationZ: wireSlope,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_wire_stairs_rotated`,
                            x: s.leftZ,
                            y: leftWireY,
                            z: leftFixingPartX,
                            rotationY: -Math.PI / 2,
                            rotationZ: wireSlope,
                            fileUrl: wireObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.wireObjectName,
                            material: s.$fixingSystemAttachementData.material,
                            morphTargetInfluences: {
                                length: {
                                    value: wireLength,
                                    scaleZero: s.$fixingSystemAttachementData.scaleZero,
                                    scaleOne: s.$fixingSystemAttachementData.scaleOne
                                }
                            }
                        },
                        {
                            key: `${s.key}_right_stairs_rotated`,
                            x: s.leftZ,
                            y: rightWireY,
                            z: rightFixingPartX,
                            rotationY: Math.PI / 2,
                            rotationZ: -wireSlope,
                            fileUrl: fixingSystemObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.fixingSystemObjectName,
                            material: s.$fixingSystemAttachementData.material
                        }
                    ]
                }
            ];
        },
        $stairsAdaptorParts: (s)=>{
            const adaptorScrewSize = 7;
            const leftScrew = {
                x: s.leftZ,
                y: s.leftY + s.$offsetFromBottom,
                z: s.leftX + s.$postSize / 2 + s.$fixingSystemAttachementData.partCenterOffset + adaptorScrewSize
            };
            const leftHandler = {
                x: leftScrew.x,
                y: leftScrew.y,
                z: leftScrew.z
            };
            const totalAdaptorSize = 20;
            const rightFixingPartZ = s.rightX - s.$postSize / 2 - s.$fixingSystemAttachementData.partCenterOffset - adaptorScrewSize;
            const leftWireY = s.leftY + s.$offsetFromBottom;
            const rightWireY = s.rightY + s.$offsetFromBottom;
            const wireLength = Math.sqrt((rightFixingPartZ - leftHandler.z - totalAdaptorSize) ** 2 + (rightWireY - leftWireY) ** 2);
            const wireSlope = Math.atan((rightWireY - leftWireY) / (rightFixingPartZ - leftHandler.z));
            const wire = {
                x: leftScrew.x,
                y: leftScrew.y + Math.sin(wireSlope) * totalAdaptorSize,
                z: leftScrew.z + Math.cos(wireSlope) * totalAdaptorSize
            };
            const wireObjectInfo = getObjectInfo(s.$fixingSystemAttachementData.wireObjectName);
            const adaptorHandlerObjectInfo = getObjectInfo(ObjectNames.wireStainlessSteel);
            const adaptorScrewObjectInfo = getObjectInfo(ObjectNames.wireStainlessSteelRotatable);
            return [
                {
                    skus: s.$partSkus,
                    objects: [
                        {
                            key: `${s.key}_left_stairs_adaptor_screw`,
                            x: leftScrew.x,
                            y: leftScrew.y,
                            z: leftScrew.z,
                            fileUrl: adaptorScrewObjectInfo.fileUrl,
                            objectName: ObjectNames.wireStainlessSteelRotatable,
                            rotationY: Math.PI / 2,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_left_stairs_adaptor_handler`,
                            x: leftHandler.x,
                            y: leftHandler.y,
                            z: leftHandler.z,
                            fileUrl: adaptorHandlerObjectInfo.fileUrl,
                            objectName: ObjectNames.wireStainlessSteel,
                            rotationY: Math.PI / 2,
                            rotationZ: -wireSlope,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_wire_stairs_adaptor`,
                            x: wire.x,
                            y: wire.y,
                            z: wire.z,
                            fileUrl: wireObjectInfo.fileUrl,
                            objectName: s.$fixingSystemAttachementData.wireObjectName,
                            material: s.$fixingSystemAttachementData.material,
                            rotationY: -Math.PI / 2,
                            rotationZ: wireSlope,
                            morphTargetInfluences: {
                                length: {
                                    value: wireLength,
                                    scaleZero: s.$fixingSystemAttachementData.scaleZero,
                                    scaleOne: s.$fixingSystemAttachementData.scaleOne
                                }
                            }
                        },
                        {
                            key: `${s.key}_right_stairs_adaptor_handler`,
                            x: leftHandler.x,
                            y: rightWireY,
                            z: rightFixingPartZ,
                            fileUrl: adaptorHandlerObjectInfo.fileUrl,
                            objectName: ObjectNames.wireStainlessSteel,
                            rotationY: -Math.PI / 2,
                            rotationZ: wireSlope,
                            material: s.$fixingSystemAttachementData.material
                        },
                        {
                            key: `${s.key}_right_stairs_adaptor_screw`,
                            x: leftScrew.x,
                            y: rightWireY,
                            z: rightFixingPartZ,
                            fileUrl: adaptorScrewObjectInfo.fileUrl,
                            objectName: ObjectNames.wireStainlessSteelRotatable,
                            rotationY: -Math.PI / 2,
                            material: s.$fixingSystemAttachementData.material
                        }
                    ]
                }
            ];
        },
        $parts: FixingSystemDataBindings.parts.bind()
    },
    Handrail: {
        $handrailPartData: HandrailBindings.handrailPartData.bind(),
        $numberOfHandrailSkus: (s)=>{
            return Math.floor(s.balustradeLength / 1000);
        },
        $skuLength: (s)=>{
            return s.balustradeLength - s.$postSize;
        },
        $handrailSkuMetadata: (s)=>{
            return {
                length: s.$skuLength
            };
        },
        $handrailSkus: (s)=>{
            const handrailSku = s.$handrailPartData.handrailSku;
            if (!handrailSku) {
                return [];
            }
            return Array.from({
                length: s.$numberOfHandrailSkus
            }, ()=>({
                    sku: handrailSku,
                    metadata: s.$handrailSkuMetadata
                }));
        },
        $parts: (s)=>{
            const sectionStep = s.length / s.numberOfSections;
            const skus = [
                ...s.$handrailSkus
            ];
            const objects = [];
            // Edge cases during dragging may blow up length => these are transient states, but we don't want to run out of memory
            if (s.numberOfSections > 100) {
                return [];
            }
            for(let i = 0; i < s.numberOfSections; i++){
                const left = s.x + i * sectionStep;
                const right = s.x + (i + 1) * sectionStep;
                const length = right - left;
                if (i !== s.numberOfSections - 1 && s.$handrailPartData.joinerObjectName && s.$handrailPartData.joinerSku) {
                    skus.push({
                        sku: s.$handrailPartData.joinerSku,
                        metadata: {}
                    });
                    const joinerObjectInfo = getObjectInfo(s.$handrailPartData.joinerObjectName);
                    objects.push({
                        objectName: s.$handrailPartData.joinerObjectName,
                        fileUrl: joinerObjectInfo.fileUrl,
                        material: s.$handrailPartData.material,
                        key: `${s.key}_joiner_${i}`,
                        x: left + length,
                        y: s.y,
                        z: s.z,
                        rotationY: s.rotationY,
                        rotationX: s.rotationX,
                        rotationZ: s.rotationZ
                    });
                }
                const angleValue = s.$handrailPartData.angled ? -s.rotationZ : 0;
                if (s.$handrailPartData.handrailObjectName) {
                    const handrailObjectInfo = getObjectInfo(s.$handrailPartData.handrailObjectName);
                    objects.push({
                        objectName: s.$handrailPartData.handrailObjectName,
                        fileUrl: handrailObjectInfo.fileUrl,
                        material: s.$handrailPartData.material,
                        key: `${s.key}_${i}`,
                        x: left,
                        y: s.y,
                        z: s.z,
                        rotationY: s.rotationY,
                        rotationX: s.rotationX,
                        rotationZ: s.rotationZ,
                        morphTargetInfluences: {
                            length: {
                                value: length,
                                scaleZero: 100,
                                scaleOne: 10000
                            },
                            angle: {
                                value: angleValue,
                                scaleZero: 0,
                                scaleOne: Math.PI / 4
                            }
                        }
                    });
                }
            }
            const parts = [
                {
                    skus,
                    objects
                }
            ];
            return parts;
        }
    },
    HandrailEnd: {
        $parts: HandrailEndBindings.parts.bind(),
        $selectionFilter: (s)=>{
            if (s.$deckItem.$deck.handrailType === HandrailType.FlatTimber) {
                return false;
            }
            if (s.positionType === PositionType.LeftEnd || s.positionType === PositionType.RightEnd) {
                return true;
            }
            return false;
        }
    }
});

//# sourceMappingURL=projected-entity.js.map